import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  HelpCircle, Calendar, AlertTriangle, MessageCircle, Clock, 
  FileText, Phone, Mail, ChevronRight, CheckCircle, XCircle,
  RefreshCw, Package, Truck, Shield
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { toast } from 'sonner';
import { generateWhatsAppLink, trackWhatsAppClick } from '@/utils/whatsappTemplates';
import { siteContent } from '@/data/siteContent';
import { CONTACT_INFO } from '@/data/mockData';
import api from '@/services/api';

const { global } = siteContent;

const supportCategories = [
  {
    id: 'track',
    title: 'Track My Delivery',
    description: 'Get real-time status updates on your booking',
    icon: Package,
    color: 'bg-blue-100 text-blue-600',
    action: 'track'
  },
  {
    id: 'reschedule',
    title: 'Reschedule Booking',
    description: 'Change the date or time of your delivery',
    icon: Calendar,
    color: 'bg-green-100 text-green-600',
    action: 'form'
  },
  {
    id: 'cancel',
    title: 'Cancel Booking',
    description: 'Cancel your upcoming delivery or move',
    icon: XCircle,
    color: 'bg-red-100 text-red-600',
    action: 'form'
  },
  {
    id: 'damage',
    title: 'Report Damage',
    description: 'File a claim for damaged items',
    icon: AlertTriangle,
    color: 'bg-orange-100 text-orange-600',
    action: 'form'
  },
  {
    id: 'complaint',
    title: 'Lodge Complaint',
    description: 'Report an issue with our service',
    icon: FileText,
    color: 'bg-purple-100 text-purple-600',
    action: 'form'
  },
  {
    id: 'general',
    title: 'General Inquiry',
    description: 'Other questions or feedback',
    icon: HelpCircle,
    color: 'bg-gray-100 text-gray-600',
    action: 'form'
  }
];

const supportFAQs = [
  {
    question: 'How do I track my delivery?',
    answer: 'Enter your booking reference (e.g., DLV-12345 or MNM-12345) in the tracking section above, or message us on WhatsApp with your reference number for instant updates.'
  },
  {
    question: 'Can I reschedule my booking?',
    answer: 'Yes! You can reschedule up to 2 hours before your scheduled pickup time at no extra charge. Simply use the reschedule form above or message us on WhatsApp.'
  },
  {
    question: 'What is your cancellation policy?',
    answer: 'Cancellations made more than 2 hours before pickup are free. Cancellations within 2 hours may incur a R50 fee. No-shows are charged in full.'
  },
  {
    question: 'How do I file a damage claim?',
    answer: 'Use the damage report form above within 24 hours of delivery. Include photos of the damage and your booking reference. We\'ll review your claim within 48 hours.'
  },
  {
    question: 'What items are covered for damage?',
    answer: 'We cover damage caused during transit due to our handling. Items must be properly packaged. Fragile items should be declared. Maximum claim is R5,000 per booking.'
  },
  {
    question: 'How long do refunds take?',
    answer: 'Approved refunds are processed within 3-5 business days to your original payment method.'
  }
];

const Support = () => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [trackingRef, setTrackingRef] = useState('');
  const [trackingResult, setTrackingResult] = useState(null);
  const [isTracking, setIsTracking] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    bookingRef: '',
    category: '',
    subject: '',
    message: '',
    newDateTime: '',
    reason: ''
  });

  const handleWhatsAppClick = (templateName, variables = {}) => {
    trackWhatsAppClick({ templateName, page: 'support' });
    const link = generateWhatsAppLink(templateName, variables);
    window.open(link, '_blank');
  };

  const handleTrack = async (e) => {
    e.preventDefault();
    if (!trackingRef.trim()) {
      toast.error('Please enter a booking reference');
      return;
    }

    setIsTracking(true);
    try {
      const response = await api.get(`/api/track/${trackingRef}`);
      if (response.data.success) {
        setTrackingResult(response.data.booking);
        toast.success('Booking found!');
      }
    } catch (error) {
      // For demo, show WhatsApp option
      toast.error('Booking not found. Try WhatsApp for live updates.');
      setTrackingResult(null);
    } finally {
      setIsTracking(false);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Submit support ticket to backend
      const ticketData = {
        ...formData,
        category: selectedCategory,
        created_at: new Date().toISOString()
      };

      await api.post('/api/support/tickets', ticketData);
      toast.success('Support request submitted! We\'ll respond within 24 hours.');

      // Offer WhatsApp for faster response
      const shouldWhatsApp = window.confirm('Would you like to continue on WhatsApp for a faster response?');
      if (shouldWhatsApp) {
        handleWhatsAppClick('support', {
          issue: `${selectedCategory}: ${formData.subject}`,
          refId: formData.bookingRef
        });
      }

      // Reset form
      setFormData({
        name: '',
        email: '',
        mobile: '',
        bookingRef: '',
        category: '',
        subject: '',
        message: '',
        newDateTime: '',
        reason: ''
      });
      setSelectedCategory(null);
    } catch (error) {
      // If backend fails, redirect to WhatsApp
      toast.error('Submission failed. Redirecting to WhatsApp...');
      handleWhatsAppClick('support', {
        issue: `${selectedCategory}: ${formData.subject || formData.message}`,
        refId: formData.bookingRef
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderCategoryForm = () => {
    if (!selectedCategory) return null;

    const category = supportCategories.find(c => c.id === selectedCategory);
    
    return (
      <Card className="mt-8" data-testid="support-form">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${category.color}`}>
              <category.icon className="h-5 w-5" />
            </div>
            <div>
              <CardTitle>{category.title}</CardTitle>
              <CardDescription>{category.description}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleFormSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Your Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  data-testid="support-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="mobile">Mobile Number *</Label>
                <Input
                  id="mobile"
                  type="tel"
                  value={formData.mobile}
                  onChange={(e) => setFormData({...formData, mobile: e.target.value})}
                  placeholder="+27"
                  required
                  data-testid="support-mobile"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  data-testid="support-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bookingRef">Booking Reference *</Label>
                <Input
                  id="bookingRef"
                  value={formData.bookingRef}
                  onChange={(e) => setFormData({...formData, bookingRef: e.target.value})}
                  placeholder="e.g., DLV-12345"
                  required
                  data-testid="support-ref"
                />
              </div>
            </div>

            {selectedCategory === 'reschedule' && (
              <div className="space-y-2">
                <Label htmlFor="newDateTime">New Preferred Date & Time *</Label>
                <Input
                  id="newDateTime"
                  type="datetime-local"
                  value={formData.newDateTime}
                  onChange={(e) => setFormData({...formData, newDateTime: e.target.value})}
                  required
                  data-testid="support-datetime"
                />
              </div>
            )}

            {(selectedCategory === 'cancel' || selectedCategory === 'reschedule') && (
              <div className="space-y-2">
                <Label htmlFor="reason">Reason</Label>
                <Select 
                  value={formData.reason} 
                  onValueChange={(value) => setFormData({...formData, reason: value})}
                >
                  <SelectTrigger data-testid="support-reason">
                    <SelectValue placeholder="Select a reason" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="schedule_conflict">Schedule conflict</SelectItem>
                    <SelectItem value="plans_changed">Plans changed</SelectItem>
                    <SelectItem value="found_alternative">Found alternative</SelectItem>
                    <SelectItem value="price_concern">Price concern</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {selectedCategory === 'damage' && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <p className="text-sm text-orange-800">
                  <strong>Important:</strong> Please have photos of the damage ready. 
                  After submitting this form, you'll be prompted to send photos via WhatsApp.
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="message">
                {selectedCategory === 'damage' ? 'Describe the damage *' : 'Additional Details'}
              </Label>
              <Textarea
                id="message"
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                placeholder={selectedCategory === 'damage' 
                  ? 'Describe what was damaged, when you noticed it, and the extent of the damage...'
                  : 'Any additional information that might help us assist you...'}
                rows={4}
                required={selectedCategory === 'damage'}
                data-testid="support-message"
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button 
                type="submit" 
                className="bg-[#001A47] hover:bg-[#002B6B] flex-1"
                disabled={isSubmitting}
                data-testid="support-submit"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Request'}
              </Button>
              <Button 
                type="button"
                variant="outline"
                className="border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white flex-1"
                onClick={() => handleWhatsAppClick(
                  selectedCategory === 'damage' ? 'damageClaim' : 
                  selectedCategory === 'reschedule' ? 'reschedule' :
                  selectedCategory === 'cancel' ? 'cancel' : 'support',
                  {
                    refId: formData.bookingRef,
                    issue: formData.message,
                    newDatetime: formData.newDateTime,
                    reason: formData.reason,
                    description: formData.message
                  }
                )}
                data-testid="support-whatsapp"
              >
                <MessageCircle className="mr-2 h-4 w-4" />
                Continue on WhatsApp
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen" data-testid="support-page">
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#001A47] via-[#002B6B] to-[#001A47] text-white py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <Shield className="h-16 w-16 text-[#039fff] mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Support Center</h1>
            <p className="text-xl text-gray-300 mb-6">
              We're here to help. Track your delivery, reschedule, or get assistance.
            </p>
            <div className="flex items-center justify-center gap-4 text-sm text-gray-400">
              <span className="flex items-center">
                <Clock className="h-4 w-4 mr-1" />
                Response within 24h
              </span>
              <span className="flex items-center">
                <MessageCircle className="h-4 w-4 mr-1" />
                WhatsApp: Instant
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Track */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <Card>
              <CardHeader className="text-center">
                <CardTitle className="flex items-center justify-center gap-2">
                  <Package className="h-5 w-5 text-[#039fff]" />
                  Track Your Delivery
                </CardTitle>
                <CardDescription>Enter your booking reference to get status updates</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleTrack} className="flex gap-3">
                  <Input
                    value={trackingRef}
                    onChange={(e) => setTrackingRef(e.target.value.toUpperCase())}
                    placeholder="e.g., DLV-12345 or MNM-12345"
                    className="flex-1"
                    data-testid="track-input"
                  />
                  <Button 
                    type="submit" 
                    className="bg-[#039fff] hover:bg-[#0380cc]"
                    disabled={isTracking}
                    data-testid="track-btn"
                  >
                    {isTracking ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Track'}
                  </Button>
                </form>

                {trackingResult && (
                  <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center gap-2 text-green-700 mb-2">
                      <CheckCircle className="h-5 w-5" />
                      <span className="font-semibold">Booking Found</span>
                    </div>
                    <p className="text-sm text-gray-600">
                      Status: <span className="font-medium">{trackingResult.status}</span>
                    </p>
                  </div>
                )}

                <div className="mt-4 text-center">
                  <Button
                    variant="link"
                    className="text-[#25D366]"
                    onClick={() => handleWhatsAppClick('track', { bookingRef: trackingRef || '__' })}
                  >
                    <MessageCircle className="h-4 w-4 mr-1" />
                    Get live updates on WhatsApp
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Support Categories */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#001A47] mb-4">How Can We Help?</h2>
            <p className="text-gray-600">Select a category below to get started</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {supportCategories.map((category) => (
              <Card 
                key={category.id}
                className={`cursor-pointer transition-all hover:shadow-lg ${
                  selectedCategory === category.id ? 'ring-2 ring-[#039fff]' : ''
                }`}
                onClick={() => setSelectedCategory(category.id)}
                data-testid={`support-cat-${category.id}`}
              >
                <CardContent className="p-6">
                  <div className={`p-3 rounded-lg ${category.color} w-fit mb-4`}>
                    <category.icon className="h-6 w-6" />
                  </div>
                  <h3 className="font-semibold text-[#001A47] mb-2">{category.title}</h3>
                  <p className="text-sm text-gray-600">{category.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Category Form */}
          <div className="max-w-2xl mx-auto">
            {renderCategoryForm()}
          </div>
        </div>
      </section>

      {/* Contact Options */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-[#001A47] text-center mb-8">Other Ways to Reach Us</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="text-center">
                <CardContent className="p-6">
                  <div className="bg-[#25D366]/10 rounded-full p-4 w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <MessageCircle className="h-8 w-8 text-[#25D366]" />
                  </div>
                  <h3 className="font-semibold mb-2">WhatsApp</h3>
                  <p className="text-sm text-gray-600 mb-3">Fastest response</p>
                  <Button 
                    className="w-full bg-[#25D366] hover:bg-[#22c55e]"
                    onClick={() => handleWhatsAppClick('support', { issue: 'General inquiry' })}
                  >
                    Message Us
                  </Button>
                </CardContent>
              </Card>

              <Card className="text-center">
                <CardContent className="p-6">
                  <div className="bg-[#039fff]/10 rounded-full p-4 w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <Phone className="h-8 w-8 text-[#039fff]" />
                  </div>
                  <h3 className="font-semibold mb-2">Call Us</h3>
                  <p className="text-sm text-gray-600 mb-3">{global.operatingHours.split('|')[0]}</p>
                  <a href={`tel:${CONTACT_INFO.phone}`}>
                    <Button variant="outline" className="w-full">
                      {global.phone}
                    </Button>
                  </a>
                </CardContent>
              </Card>

              <Card className="text-center">
                <CardContent className="p-6">
                  <div className="bg-[#001A47]/10 rounded-full p-4 w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <Mail className="h-8 w-8 text-[#001A47]" />
                  </div>
                  <h3 className="font-semibold mb-2">Email</h3>
                  <p className="text-sm text-gray-600 mb-3">For formal inquiries</p>
                  <a href={`mailto:${global.email}`}>
                    <Button variant="outline" className="w-full">
                      {global.email}
                    </Button>
                  </a>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Support FAQs */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-[#001A47] text-center mb-8">Frequently Asked Questions</h2>
            <Accordion type="single" collapsible className="w-full">
              {supportFAQs.map((faq, index) => (
                <AccordionItem key={index} value={`faq-${index}`}>
                  <AccordionTrigger className="text-left">{faq.question}</AccordionTrigger>
                  <AccordionContent className="text-gray-600">{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-12 bg-[#001A47] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold mb-4">Still Need Help?</h2>
          <p className="text-gray-300 mb-6">Our team is ready to assist you</p>
          <Button 
            size="lg"
            className="bg-[#fb6402] hover:bg-[#e55a02]"
            onClick={() => handleWhatsAppClick('support', { issue: 'Need assistance' })}
          >
            <MessageCircle className="mr-2 h-5 w-5" />
            Chat with Support
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Support;
